/**
 * useVoiceNotes Hook
 * 
 * Manages voice note recording, playback, upload and deletion.
 * Web: uses MediaRecorder API (via useVoiceNotesWeb).
 * Native: uses expo-av (lazy-loaded).
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Platform } from 'react-native';
import * as Location from 'expo-location';
import { authApi, VoiceNote } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import { useWebAudio } from './useVoiceNotesWeb';

interface UseVoiceNotesProps {
  portfolioId: number;
  opportunityId: number;
}

export function useVoiceNotes({ portfolioId, opportunityId }: UseVoiceNotesProps) {
  const [notes, setNotes] = useState<VoiceNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);

  const nativeRecordingRef = useRef<any>(null);
  const nativeSoundRef = useRef<any>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationRef = useRef(0);

  const fetchNotes = useCallback(async () => {
    if (!portfolioId || !opportunityId) return;
    try {
      setLoading(true);
      const response = await authApi.getVoiceNotes(portfolioId, opportunityId);
      if (response.success) setNotes(response.voiceNotes);
    } catch (err) {
      logger.error('Failed to fetch voice notes', err, 'useVoiceNotes');
    } finally {
      setLoading(false);
    }
  }, [portfolioId, opportunityId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const getCurrentLocation = useCallback(async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') return null;
      const loc = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced });
      return { latitude: loc.coords.latitude, longitude: loc.coords.longitude };
    } catch { return null; }
  }, []);

  const startDurationTimer = useCallback(() => {
    setRecordingDuration(0);
    durationRef.current = 0;
    durationIntervalRef.current = setInterval(() => {
      durationRef.current += 1;
      setRecordingDuration(durationRef.current);
    }, 1000);
  }, []);

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  // Web audio callbacks
  const webCallbacks = useMemo(() => ({
    onRecordingStarted: () => { setRecording(true); setError(null); startDurationTimer(); },
    onRecordingStopped: () => { setRecording(false); stopDurationTimer(); },
    onUploadStart: () => setUploading(true),
    onUploadEnd: () => { setUploading(false); setRecordingDuration(0); },
    onError: (msg: string) => setError(msg),
    onPlayStart: (noteId: number) => setPlaying(noteId),
    onPlayEnd: () => setPlaying(null),
    getLocation: getCurrentLocation,
    getDuration: () => durationRef.current,
    fetchNotes,
  }), [startDurationTimer, stopDurationTimer, getCurrentLocation, fetchNotes]);

  const web = useWebAudio(portfolioId, opportunityId, webCallbacks);

  // Native recording (expo-av) - iOS/Android only
  const startRecordingNative = useCallback(async () => {
    try {
      setError(null);
      const { Audio } = require('expo-av');
      const { status } = await Audio.requestPermissionsAsync();
      if (status !== 'granted') { setError('Microphone permission required'); return; }
      await Audio.setAudioModeAsync({ allowsRecordingIOS: true, playsInSilentModeIOS: true });
      const { recording: rec } = await Audio.Recording.createAsync(Audio.RecordingOptionsPresets.HIGH_QUALITY);
      nativeRecordingRef.current = rec;
      setRecording(true);
      startDurationTimer();
    } catch (err: any) {
      logger.error('Native recording failed', err, 'useVoiceNotes');
      setError(err?.message || 'Failed to start recording');
    }
  }, [startDurationTimer]);

  const stopRecordingNative = useCallback(async () => {
    if (!nativeRecordingRef.current) return;
    stopDurationTimer();
    setRecording(false);
    setUploading(true);
    try {
      const { Audio } = require('expo-av');
      const FileSystem = require('expo-file-system');
      await nativeRecordingRef.current.stopAndUnloadAsync();
      const uri = nativeRecordingRef.current.getURI();
      const status = await nativeRecordingRef.current.getStatusAsync();
      nativeRecordingRef.current = null;
      await Audio.setAudioModeAsync({ allowsRecordingIOS: false });
      if (!uri) { setUploading(false); return; }
      const base64 = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
      const location = await getCurrentLocation();
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      await authApi.uploadVoiceNote(portfolioId, opportunityId, {
        audio_data: base64, filename: `voice-note-${ts}.m4a`, mime_type: 'audio/m4a',
        duration_ms: status.durationMillis || durationRef.current * 1000,
        latitude: location?.latitude, longitude: location?.longitude,
      });
      await fetchNotes();
    } catch (err) {
      logger.error('Failed to stop/upload native recording', err, 'useVoiceNotes');
      setError('Failed to upload recording');
    } finally {
      setUploading(false); setRecordingDuration(0);
    }
  }, [portfolioId, opportunityId, stopDurationTimer, getCurrentLocation, fetchNotes]);

  const playNoteNative = useCallback(async (noteId: number) => {
    try {
      const { Audio } = require('expo-av');
      if (nativeSoundRef.current) { await nativeSoundRef.current.stopAsync(); await nativeSoundRef.current.unloadAsync(); }
      setPlaying(noteId);
      const resp = await authApi.getVoiceNote(portfolioId, opportunityId, noteId);
      if (!resp.success || !resp.voiceNote.audio_data) { setPlaying(null); return; }
      await Audio.setAudioModeAsync({ allowsRecordingIOS: false, playsInSilentModeIOS: true });
      const { sound } = await Audio.Sound.createAsync({ uri: `data:${resp.voiceNote.mime_type};base64,${resp.voiceNote.audio_data}` });
      nativeSoundRef.current = sound;
      sound.setOnPlaybackStatusUpdate((s: any) => {
        if (s.didJustFinish) { setPlaying(null); sound.unloadAsync(); nativeSoundRef.current = null; }
      });
      await sound.playAsync();
    } catch (err) {
      logger.error('Failed to play voice note (native)', err, 'useVoiceNotes');
      setPlaying(null);
    }
  }, [portfolioId, opportunityId]);

  const stopPlaybackNative = useCallback(async () => {
    if (nativeSoundRef.current) { await nativeSoundRef.current.stopAsync(); await nativeSoundRef.current.unloadAsync(); nativeSoundRef.current = null; }
    setPlaying(null);
  }, []);

  // Platform dispatch
  const isWeb = Platform.OS === 'web';
  const startRecording = useCallback(() => isWeb ? web.startRecording() : startRecordingNative(), [isWeb, web, startRecordingNative]);
  const stopRecording = useCallback(() => isWeb ? web.stopRecording() : stopRecordingNative(), [isWeb, web, stopRecordingNative]);
  const playNote = useCallback((id: number) => isWeb ? web.playNote(id) : playNoteNative(id), [isWeb, web, playNoteNative]);
  const stopPlayback = useCallback(() => isWeb ? web.stopPlayback() : stopPlaybackNative(), [isWeb, web, stopPlaybackNative]);

  const deleteNote = useCallback(async (noteId: number) => {
    try {
      await authApi.deleteVoiceNote(portfolioId, opportunityId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      logger.error('Failed to delete voice note', err, 'useVoiceNotes');
      setError('Failed to delete voice note');
    }
  }, [portfolioId, opportunityId]);

  useEffect(() => {
    return () => {
      stopDurationTimer();
      if (isWeb) web.cleanup();
      if (nativeSoundRef.current) nativeSoundRef.current.unloadAsync().catch(() => {});
      if (nativeRecordingRef.current) nativeRecordingRef.current.stopAndUnloadAsync().catch(() => {});
    };
  }, [isWeb, web, stopDurationTimer]);

  return {
    notes, loading, recording, playing, uploading, error, recordingDuration,
    fetchNotes, startRecording, stopRecording, playNote, stopPlayback, deleteNote,
  };
}
